import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Checkbox,
  FormGroup,
  FormControlLabel,
  FormControl,
  FormLabel
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { useToast } from '../context/ToastContext';
import { adminAPI } from '../services/api';
import { formatBookingCodeForDisplay } from '../utils/formatBookingCodeDisplay.js';
import { assetKeyFromUrl } from '../utils/assetCdnUrl.js';

const s3KeyFromUrl = assetKeyFromUrl;

function formatDateKey(d) {
  if (!d) return '';
  const x = new Date(d);
  if (isNaN(x.getTime())) return '';
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function defaultGroupName(row) {
  if (!row) return '';
  const code = row.batch?.code;
  if (code && String(code).trim()) return String(code).trim();
  const trek = row.batch?.trek?.name || '';
  const start = formatDateKey(row.batch?.startDate);
  return [trek, start].filter(Boolean).join(' · ').slice(0, 80);
}

function normalizeTicketFiles(raw) {
  const x = raw && typeof raw === 'object' ? raw : {};
  return {
    departure: Array.isArray(x.departure) ? x.departure : [],
    return: Array.isArray(x.return) ? x.return : [],
    departurePnr: String(x.departurePnr ?? x.departure_pnr ?? '').trim(),
    returnPnr: String(x.returnPnr ?? x.return_pnr ?? '').trim()
  };
}

function ticketItemId(item) {
  return String(item?.id || item?.key || item?.url || '').trim();
}

function isImageMime(mime) {
  return String(mime || '').toLowerCase().startsWith('image/');
}

export default function EditTicketGroupModal({ open, row, onClose, onSaved, canManage = true }) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [groupName, setGroupName] = useState('');
  const [leg, setLeg] = useState('return');
  const [departurePnr, setDeparturePnr] = useState('');
  const [returnPnr, setReturnPnr] = useState('');
  const [ticketFiles, setTicketFiles] = useState({ departure: [], return: [], departurePnr: '', returnPnr: '' });
  const [uploading, setUploading] = useState(false);
  const [savingPnr, setSavingPnr] = useState(false);
  const [selectedBookingIds, setSelectedBookingIds] = useState(new Set());
  const [deletingId, setDeletingId] = useState('');
  const [downloadingId, setDownloadingId] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewMime, setPreviewMime] = useState('');
  const [previewItemId, setPreviewItemId] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const { data: bookingDetailRes } = useQuery({
    queryKey: ['admin-ticket-group-booking', row?.id],
    queryFn: () => adminAPI.getBooking(row.id),
    enabled: open && !!row?.id,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true
  });
  const bookingDetail = bookingDetailRes?.data?.data || null;

  const clearPreview = useCallback(() => {
    setPreviewUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    setPreviewMime('');
    setPreviewItemId('');
    setPreviewLoading(false);
  }, []);

  useEffect(() => {
    if (!open || !row) return;
    setGroupName(defaultGroupName(row));
    setLeg('return');
    const tf = normalizeTicketFiles(row.ticketFiles || row.ticket_files);
    setTicketFiles(tf);
    setDeparturePnr(tf.departurePnr);
    setReturnPnr(tf.returnPnr);
    setUploading(false);
    setSavingPnr(false);
    setDeletingId('');
    setDownloadingId('');
    clearPreview();
    setApiError('');
  }, [open, row?.id, clearPreview]);

  useEffect(() => {
    if (!open || !bookingDetail) return;
    const tf = normalizeTicketFiles(bookingDetail.ticketFiles || bookingDetail.ticket_files);
    setTicketFiles(tf);
    setDeparturePnr(tf.departurePnr);
    setReturnPnr(tf.returnPnr);
    const savedGroupName = String(bookingDetail.groupName || bookingDetail.group_name || '').trim();
    if (savedGroupName) setGroupName(savedGroupName);
    
    if (bookingDetail.groupBookings && bookingDetail.groupBookings.length > 0) {
      const initialIds = new Set();
      for (const b of bookingDetail.groupBookings) {
        if (!['pending', 'cancelled'].includes(b.status)) {
          initialIds.add(String(b.id));
        }
      }
      setSelectedBookingIds(initialIds);
    } else {
      setSelectedBookingIds(new Set([String(row?.id)]));
    }

    const hasTicketFilesField =
      Object.prototype.hasOwnProperty.call(bookingDetail, 'ticketFiles')
      || Object.prototype.hasOwnProperty.call(bookingDetail, 'ticket_files');
    if (!hasTicketFilesField) {
      setApiError(
        'Backend response does not include ticket_files yet. Deploy latest backend + run booking ticket files migration.'
      );
    }
  }, [open, bookingDetail?.id, bookingDetail?.updatedAt]);

  const handleClose = () => {
    clearPreview();
    onClose();
  };

  const loadTicketPreview = async (item) => {
    if (!row?.id || !item) return;
    const id = ticketItemId(item);
    const key = String(item.key || s3KeyFromUrl(item.url) || '').trim();
    if (!key && item.url) {
      clearPreview();
      setPreviewUrl(item.url);
      setPreviewMime(item.mimeType || '');
      setPreviewItemId(id);
      return;
    }
    if (!key) return;
    setPreviewLoading(true);
    setPreviewItemId(id);
    try {
      const res = await adminAPI.downloadBookingTicketFile(row.id, { key });
      const mime = res.headers['content-type'] || item.mimeType || 'application/pdf';
      const blob = new Blob([res.data], { type: mime });
      setPreviewUrl((prev) => {
        if (prev && !prev.startsWith('http')) URL.revokeObjectURL(prev);
        return URL.createObjectURL(blob);
      });
      setPreviewMime(mime);
      setApiError('');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to load ticket preview.';
      setApiError(msg);
      showToast({ message: msg, severity: 'error' });
      setPreviewItemId('');
    } finally {
      setPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!open || canManage) return;
    const list = leg === 'departure' ? ticketFiles.departure : ticketFiles.return;
    if (!list.length) {
      clearPreview();
      return;
    }
    const firstId = ticketItemId(list[0]);
    if (previewItemId && list.some((item) => ticketItemId(item) === previewItemId)) return;
    loadTicketPreview(list[0]);
  }, [open, canManage, leg, ticketFiles.departure, ticketFiles.return]);

  const openFilePicker = () => {
    const pnr = leg === 'departure' ? departurePnr.trim() : returnPnr.trim();
    if (!pnr) {
      showToast({
        message: `Enter ${leg === 'departure' ? 'DPNR' : 'RPNR'} before uploading (used as the PDF file name).`,
        severity: 'warning'
      });
      return;
    }
    fileInputRef.current?.click();
  };

  const saveLegPnr = async (nextLeg, nextPnr) => {
    if (!row?.id) return;
    setSavingPnr(true);
    try {
      const payload = { 
        leg: nextLeg, 
        pnr: nextPnr.trim(),
        applyToBookingIds: JSON.stringify(Array.from(selectedBookingIds))
      };
      const res = await adminAPI.updateBookingTicketLegPnr(row.id, payload);
      const tf = normalizeTicketFiles(res?.data?.data?.ticketFiles);
      setTicketFiles(tf);
      setDeparturePnr(tf.departurePnr);
      setReturnPnr(tf.returnPnr);
      queryClient.invalidateQueries(['admin-ticket-group-booking', row.id]);
      queryClient.invalidateQueries('admin-ticket-bookings');
      setApiError('');
      onSaved?.();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save PNR.';
      setApiError(msg);
      showToast({ message: msg, severity: 'error' });
    } finally {
      setSavingPnr(false);
    }
  };

  const onFilesSelected = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    if (!row?.id) {
      showToast({ message: 'Missing booking id.', severity: 'error' });
      return;
    }
    const key = leg === 'departure' ? 'departure' : 'return';
    const pnr = key === 'departure' ? departurePnr.trim() : returnPnr.trim();
    setUploading(true);
    let success = 0;
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const formData = new FormData();
      formData.append('file', f);
      formData.append('leg', key);
      if (pnr) formData.append('pnr', pnr);
      if (groupName.trim()) formData.append('groupName', groupName.trim());
      formData.append('applyToBookingIds', JSON.stringify(Array.from(selectedBookingIds)));
      try {
        const res = await adminAPI.uploadBookingTicketFile(row.id, formData);
        const tf = normalizeTicketFiles(res?.data?.data?.ticketFiles);
        setTicketFiles(tf);
        setDeparturePnr(tf.departurePnr);
        setReturnPnr(tf.returnPnr);
        queryClient.invalidateQueries(['admin-ticket-group-booking', row.id]);
        queryClient.invalidateQueries('admin-ticket-bookings');
        setApiError('');
        success += 1;
      } catch (err) {
        const msg = err.response?.data?.message || err.message || `Failed to upload ${f.name}`;
        const status = err.response?.status;
        setApiError(status ? `Upload failed (${status}): ${msg}` : `Upload failed: ${msg}`);
        showToast({
          message: msg,
          severity: 'error'
        });
      }
    }
    if (success > 0) {
      showToast({ message: `${success} ticket file(s) uploaded.`, severity: 'success' });
      onSaved?.();
    }
    setUploading(false);
    e.target.value = '';
  };

  const removeFile = async (id) => {
    if (!row?.id || !id) return;
    setDeletingId(id);
    try {
      const res = await adminAPI.deleteBookingTicketFile(row.id, id);
      const tf = normalizeTicketFiles(res?.data?.data?.ticketFiles);
      setTicketFiles(tf);
      setDeparturePnr(tf.departurePnr);
      setReturnPnr(tf.returnPnr);
      queryClient.invalidateQueries(['admin-ticket-group-booking', row.id]);
      queryClient.invalidateQueries('admin-ticket-bookings');
      setApiError('');
      showToast({ message: 'Ticket file removed.', severity: 'success' });
      onSaved?.();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to remove file.';
      const status = err.response?.status;
      setApiError(status ? `Delete failed (${status}): ${msg}` : `Delete failed: ${msg}`);
      showToast({ message: msg, severity: 'error' });
    } finally {
      setDeletingId('');
    }
  };

  const downloadFile = async (item) => {
    if (!row?.id || !item) return;
    const key = String(item.key || s3KeyFromUrl(item.url) || '').trim();
    if (!key) {
      if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer');
      return;
    }
    setDownloadingId(item.id || key);
    try {
      const res = await adminAPI.downloadBookingTicketFile(row.id, { key });
      const blob = new Blob([res.data], { type: res.headers['content-type'] || item.mimeType || 'application/octet-stream' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = String(res.headers['content-disposition'] || '');
      const nameMatch = disposition.match(/filename=\"?([^\";]+)\"?/i);
      a.href = href;
      a.download = nameMatch?.[1] || item.fileName || 'ticket';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to download file.';
      setApiError(msg);
      showToast({ message: msg, severity: 'error' });
    } finally {
      setDownloadingId('');
    }
  };

  const openTicketFile = (item) => {
    if (!canManage) {
      loadTicketPreview(item);
      return;
    }
    downloadFile(item);
  };

  const listForLeg = useMemo(
    () => (leg === 'departure' ? ticketFiles.departure : ticketFiles.return),
    [leg, ticketFiles.departure, ticketFiles.return]
  );
  const trek = row?.batch?.trek?.name;
  const pkg = row?.ticketPackage?.name;
  const regLabel =
    row?.registrationId != null && row.registrationId !== ''
      ? row.registrationId
      : formatBookingCodeForDisplay(row?.bookingCode) || row?.bookingCode || '—';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={canManage ? 'sm' : 'md'} fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pr: 1,
          fontWeight: 700
        }}
      >
        {canManage ? 'Edit registration group' : 'View tickets'}
        <IconButton aria-label="Close" onClick={handleClose} size="small">
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <input
          ref={fileInputRef}
          type="file"
          hidden
          multiple
          accept=".pdf,image/jpeg,image/png,image/webp,application/pdf"
          onChange={onFilesSelected}
        />

        {canManage ? (
          <>
            <TextField
              fullWidth
              size="small"
              label="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              sx={{ mb: 2 }}
              helperText="Saved with newly uploaded files from this modal."
            />
            {bookingDetail?.groupBookings?.length > 1 && (
              <FormControl component="fieldset" sx={{ mb: 2, width: '100%', p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <FormLabel component="legend" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>
                  Apply ticket to selected participants:
                </FormLabel>
                <FormGroup>
                  {bookingDetail.groupBookings.map((b) => {
                    const isPending = ['pending', 'cancelled'].includes(b.status);
                    const isChecked = selectedBookingIds.has(String(b.id));
                    return (
                      <FormControlLabel
                        key={b.id}
                        control={
                          <Checkbox
                            checked={isChecked}
                            disabled={isPending || uploading || savingPnr}
                            onChange={(e) => {
                              const next = new Set(selectedBookingIds);
                              if (e.target.checked) next.add(String(b.id));
                              else next.delete(String(b.id));
                              setSelectedBookingIds(next);
                            }}
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2" sx={{ color: isPending ? 'text.disabled' : 'text.primary' }}>
                            {b.name} <Typography component="span" variant="caption">({b.status})</Typography>
                          </Typography>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            )}
          </>
        ) : null}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Reg <strong>{regLabel}</strong>
          {trek ? (
            <>
              {' · '}
              {trek}
            </>
          ) : null}
          {pkg ? (
            <>
              {' · '}
              {pkg}
            </>
          ) : null}
        </Typography>
        {apiError ? (
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: '#b91c1c',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 1,
              px: 1,
              py: 0.75
            }}
          >
            {apiError}
          </Typography>
        ) : null}

        <Tabs
          value={leg}
          onChange={(_, v) => setLeg(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Return (RPNR)" value="return" sx={{ textTransform: 'none', fontWeight: 600 }} />
          <Tab label="Departure (DPNR)" value="departure" sx={{ textTransform: 'none', fontWeight: 600 }} />
        </Tabs>

        {canManage ? (
          <TextField
            fullWidth
            size="small"
            label={leg === 'departure' ? 'DPNR (departure PNR)' : 'RPNR (return PNR)'}
            placeholder="e.g. ABC123 — saved PDF uses this name"
            value={leg === 'departure' ? departurePnr : returnPnr}
            onChange={(e) =>
              leg === 'departure' ? setDeparturePnr(e.target.value) : setReturnPnr(e.target.value)
            }
            onBlur={() => {
              const next = leg === 'departure' ? departurePnr : returnPnr;
              const prev =
                leg === 'departure' ? ticketFiles.departurePnr : ticketFiles.returnPnr;
              if (String(next || '').trim() === String(prev || '').trim()) return;
              saveLegPnr(leg, next);
            }}
            disabled={savingPnr || uploading}
            sx={{ mb: 2 }}
            helperText="Required before upload. Ticket PDF is stored as PNR.pdf (or .jpg/.png)."
          />
        ) : (
          <Typography variant="body2" sx={{ mb: 2, fontFamily: 'monospace', fontWeight: 600 }}>
            {leg === 'departure' ? 'DPNR' : 'RPNR'}:{' '}
            {(leg === 'departure' ? departurePnr : returnPnr) || '—'}
          </Typography>
        )}

        <Box sx={{ minHeight: 120 }}>
          {listForLeg.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No files uploaded for <strong>{leg}</strong> tickets yet.
            </Typography>
          ) : (
            <List dense disablePadding sx={{ mb: 2 }}>
              {listForLeg.map((item) => {
                const itemId = ticketItemId(item);
                const isSelected = !canManage && previewItemId === itemId;
                return (
                <ListItem
                  key={item.id || itemId}
                  sx={{
                    bgcolor: isSelected ? 'primary.50' : 'action.hover',
                    borderRadius: 1,
                    mb: 0.5,
                    border: isSelected ? '1px solid' : '1px solid transparent',
                    borderColor: isSelected ? 'primary.light' : 'transparent',
                    cursor: !canManage ? 'pointer' : 'default'
                  }}
                  onClick={!canManage ? () => openTicketFile(item) : undefined}
                >
                  <ListItemText
                    primary={item.pnr || item.fileName || item.name}
                    secondary={
                      item.pnr && item.fileName && item.fileName !== item.pnr
                        ? `${item.fileName}${item.size ? ` · ${Math.round(item.size / 1024)} KB` : ''}`
                        : item.size
                          ? `${Math.round(item.size / 1024)} KB`
                          : null
                    }
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {item.url || item.key ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={(e) => {
                            e.stopPropagation();
                            openTicketFile(item);
                          }}
                          disabled={!canManage && previewLoading && previewItemId === itemId}
                        >
                          {!canManage
                            ? previewLoading && previewItemId === itemId
                              ? 'Loading…'
                              : 'View'
                            : downloadingId === (item.id || item.key)
                              ? 'Downloading...'
                              : 'Open'}
                        </Button>
                      ) : null}
                      {canManage ? (
                        <IconButton edge="end" size="small" onClick={() => removeFile(item.id)} aria-label="Remove">
                          {deletingId === item.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <DeleteOutlineRoundedIcon fontSize="small" />
                          )}
                        </IconButton>
                      ) : null}
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              );
              })}
            </List>
          )}

          {!canManage ? (
            <Box
              sx={{
                mt: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: '#f8fafc',
                minHeight: 420
              }}
            >
              {previewLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 420 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : previewUrl ? (
                isImageMime(previewMime) ? (
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Ticket"
                    sx={{ display: 'block', width: '100%', maxHeight: 520, objectFit: 'contain', bgcolor: '#fff' }}
                  />
                ) : (
                  <Box
                    component="iframe"
                    src={previewUrl}
                    title="Ticket preview"
                    sx={{ display: 'block', width: '100%', height: 520, border: 0, bgcolor: '#fff' }}
                  />
                )
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 420, px: 2 }}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Select a ticket file above to preview it here.
                  </Typography>
                </Box>
              )}
            </Box>
          ) : null}

          {canManage ? (
            <Button variant="contained" color="primary" onClick={openFilePicker} disabled={uploading || savingPnr}>
              {uploading ? 'Uploading…' : '+ Upload new ticket'}
            </Button>
          ) : null}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
